import { Button, Flex, notify } from 'front-commons/ds';
import { useDynamicLoading } from 'front-commons/hooks';
import { useEffect, useMemo, useState } from 'react';
import { replaceProducts } from 'services/basket';
import { ProductsToReplaceRequest } from 'services/basket/interfaces';
import { ProductInPromotionParams } from 'services/products/interfaces';
import useBasket from 'stores/basket';
import useDrawer from 'stores/drawer';
import usePos from 'stores/pos';
import {
	ChangingStates,
	ChangingTypes,
	Handlers,
	ProductChangeQueue,
	ProductsDataProps,
	PromotionSuggestionProps,
	QueueProps,
	SummaryProps,
} from './interfaces';
import { Typography } from 'hype-ds';
import SummaryAccordion from './components/SummaryAccordion';
import PromotionSuggestionCard from './components/PromotionSuggestionCard';
import UndoSuggestionCard from './components/UndoSuggestionCard';
import useOrderImport from 'stores/orderImport';
import Header from './components/Header';

export default function PromotionSuggestion({ data }: Partial<PromotionSuggestionProps>) {
	const { posStore } = usePos();
	const { handleGetBasketData } = useBasket();
	const { handleUpdateDrawerProps, handleCloseAllDrawers, drawerStore } = useDrawer();
	const { handleLoading } = useDynamicLoading<'SOME' | 'EVERY'>();
	const { handleAddPosPromotions, handleRemovePosSinglePromotion } = useOrderImport();

	const drawerState = useMemo(() => drawerStore.drawers.find(({ id }) => id === 'promotion-suggestion'), [drawerStore]);

	const [productsData, setProductsData] = useState({} as ProductsDataProps);
	const [changingQueue, setChangingQueue] = useState({} as ProductChangeQueue);

	const posId = posStore.selectedPos.id;
	const hasSomeLoading = handleLoading().get(['EVERY', 'SOME']);
	const replaceableQuantity = data?.length || 1;

	/** Helper to lead with product state */
	const stateHandler = {
		get(simpleProductId: string | 'EVERY') {
			return productsData[simpleProductId]?.state;
		},

		update(simpleProductId: string | 'EVERY', newState: ChangingStates, prevState?: ChangingStates) {
			if (simpleProductId === 'EVERY' && prevState) {
				setProductsData((products) => {
					const entries = Object.entries(products);

					return entries.reduce((acc, [key, value]) => {
						if (value.state !== prevState)
							return {
								...acc,
								[key]: value,
							};

						return {
							...acc,
							[key]: {
								...products[key],
								state: newState,
							},
						};
					}, {} as ProductsDataProps);
				});
				return;
			}

			setProductsData((products) => ({
				...products,
				[simpleProductId]: {
					...products[simpleProductId],
					state: newState,
				},
			}));
		},

		hasSome(state: ChangingStates) {
			return Object.values(productsData).some((item) => item.state === state);
		},
	};

	/** Helper to lead with product summary */
	const summaryHandlers = {
		update(simpleProductId: string, payload: Partial<SummaryProps>) {
			setProductsData((prevState) => ({
				...prevState,
				[simpleProductId]: {
					...prevState[simpleProductId],
					summary: {
						...prevState[simpleProductId]?.summary,
						...payload,
					},
				},
			}));
		},

		get() {
			return Object.values(productsData).reduce((acc, { simpleData: { id }, summary: tempRename }) => {
				return {
					...acc,
					[id]: tempRename,
				};
			}, {});
		},
	};

	/** Helper to lead with product updated props */
	const promotionHandlers: Handlers['promotionHandlers'] = (simpleProductId: string) => {
		const handleUpdate = (payload: Partial<{ quantity: number; distributorId: string }>) => {
			setProductsData((prevState) => ({
				...prevState,
				[simpleProductId]: {
					...prevState[simpleProductId],
					updatedData: {
						...prevState[simpleProductId].updatedData,
						...payload,
					},
				},
			}));
		};

		return {
			get: {
				quantity: productsData[simpleProductId]?.updatedData?.quantity || 1,
				distributorId: productsData[simpleProductId]?.updatedData?.distributorId,
			},
			set: {
				quantity(quantity: number) {
					handleUpdate({ quantity });
				},
				distributorId(distributorId: string) {
					handleUpdate({ distributorId });
				},
			},
			map: {
				productsInCombo(products: ProductInPromotionParams[], quantity, fixedAmount) {
					return products.map(({ productId, minimumQuantity }) => ({
						productId,
						quantity: fixedAmount ? minimumQuantity! : quantity!,
					}));
				},
			},
		};
	};

	/** Change available products */
	const handleChangeEveryInto = async (into?: ChangingTypes) => {
		const toPromotion = into === 'PROMOTION';
		const changedData = data!.reduce((acc, { simpleProductInfo, suggestion }) => {
			if (stateHandler.get(simpleProductInfo.id) !== (toPromotion ? 'READY' : 'PARTIALLY_EVERY_CHANGED')) return acc;

			const { get: updatedData, map } = promotionHandlers!(simpleProductInfo.id);

			if (toPromotion) {
				const products = map.productsInCombo(suggestion.productsInCombo, updatedData.quantity, suggestion.fixedAmount);

				const commonPayload = {
					distributorId: updatedData.distributorId,
					quantity: suggestion.fixedAmount ? updatedData.quantity : 1,
				};

				handleAddPosPromotions({
					posId,
					promotionId: suggestion.id,
					payload: {
						orderImportProductId: '',
						products,
						...commonPayload,
					},
				});

				return [
					...acc,
					{
						removeProductId: simpleProductInfo.id,
						addProduct: {
							productId: suggestion.id,
							productsInCombo: products,
							...commonPayload,
						},
					},
				];
			}

			handleRemovePosSinglePromotion({
				posId,
				promotionId: suggestion.id,
			});

			return [
				...acc,
				{
					addProduct: {
						productId: simpleProductInfo.id,
						distributorId: simpleProductInfo.currentDistributorId,
						quantity: simpleProductInfo.quantity,
					},
					removeProductId: suggestion.id,
				},
			];
		}, [] as ProductsToReplaceRequest[]);

		try {
			handleLoading().set(['EVERY']);
			await replaceProducts({ posId, products: changedData });

			stateHandler.update(
				'EVERY',
				toPromotion ? 'PARTIALLY_EVERY_CHANGED' : 'READY',
				toPromotion ? 'READY' : 'PARTIALLY_EVERY_CHANGED',
			);
			setTimeout(() => {
				handleGetBasketData({ loading: 'refetch' });
			}, 500);
		} catch {
			notify.negative({ description: 'Falha ao tentar substituir todos os produtos por suas promoções' });
		} finally {
			handleLoading().remove(['EVERY']);
		}
	};

	/** Change one product */
	const handleChangeSomeInto = async ({ changeInto, data: changingData, noFetch }: QueueProps, recursive = false) => {
		if (!recursive) {
			setChangingQueue((prevState) => ({
				...prevState,
				[changingData.removeProductId]: { changeInto, data: changingData },
			}));
		}

		if (Object.keys(changingQueue).length && !recursive) return;

		const toPromotion = changeInto === 'PROMOTION';
		const simpleProductId = toPromotion ? changingData.removeProductId : changingData.addProduct.productId;

		try {
			handleLoading().set(['SOME']);

			if (!noFetch) await replaceProducts({ posId, products: [changingData] });

			stateHandler.update(simpleProductId, toPromotion ? 'PARTIALLY_CHANGED' : 'READY');
			setTimeout(() => {
				handleGetBasketData({ loading: 'refetch' });
			}, 500);
		} catch (error) {
			notify.negative({
				description: `Não conseguimos trocar seu produto para ${toPromotion ? 'promoção' : 'produto simples'}.`,
			});

			stateHandler.update(simpleProductId, toPromotion ? 'READY' : 'CHANGED');
		} finally {
			setChangingQueue((prevState) => {
				const newQueue = { ...prevState };
				delete newQueue[changingData.removeProductId];

				const nextToFetch = Object.values(newQueue);
				if (nextToFetch.length) handleChangeSomeInto(nextToFetch[0], true);

				return newQueue;
			});

			handleLoading().remove(['SOME']);
		}
	};

	/** Mount core object when new data comes */
	useEffect(() => {
		if (!data) return;

		setProductsData(
			data.reduce((acc, { simpleProductInfo, suggestion }) => {
				const initialDistributor =
					suggestion.distributors.find(
						({ distributorId, status }) =>
							distributorId === simpleProductInfo.currentDistributorId && status !== 'UNAVAILABLE',
					) || suggestion.distributors.find(({ status }) => status !== 'UNAVAILABLE');

				return {
					...acc,
					[simpleProductInfo.id]: {
						promotionData: suggestion,
						simpleData: simpleProductInfo,
						state: 'READY',
						updatedData: {
							quantity: suggestion.fixedAmount ? 1 : simpleProductInfo.quantity,
							distributorId: initialDistributor?.distributorId || '',
						},
						summary: {
							unitTax: initialDistributor?.taxSubstitution || 0,
							discount: initialDistributor?.discount || suggestion.discountToApply,
							quantity: suggestion.fixedAmount ? 1 : simpleProductInfo.quantity,
							currentValue:
								suggestion.distributors.find(({ distributorId }) => {
									return distributorId === initialDistributor?.distributorId || '';
								})?.unitPricePromotion || 0,
							distributors: suggestion.distributors,
							basketQuantity: suggestion.fixedAmount ? 1 : simpleProductInfo.quantity,
							currentDistributorId: initialDistributor?.distributorId || '',
						},
					},
				};
			}, {} as ProductsDataProps),
		);
	}, [data]);

	/** Prevent user closing basket when some product has been changed */
	useEffect(() => {
		handleUpdateDrawerProps('promotion-suggestion', {
			onClose: hasSomeLoading ? () => {} : handleCloseAllDrawers,
		});
	}, [hasSomeLoading]);

	/** To clean drawer after closed */
	useEffect(() => {
		if (!drawerState) setProductsData({});
	}, [drawerState]);

	if (!data) return null;

	return (
		<Flex direction="column" gap="0px" height="100%" maxHeight="100vh">
			<Header suggestionQuantity={data.length} />

			<Flex
				raw={{ '& > :last-child': { border: 'none' } }}
				height="100%"
				padding="0 16px 16px"
				overflow="auto"
				direction="column"
			>
				{data.map((suggestionData, index, { length }) => {
					const isQueued = !!(
						changingQueue?.[suggestionData.simpleProductInfo.id] || changingQueue?.[suggestionData.suggestion.id]
					);

					suggestionData.suggestion.distributors = suggestionData.suggestion.distributors.sort((distributor) => {
						if (distributor.distributorId === suggestionData.simpleProductInfo.currentDistributorId) return -1;

						return 1;
					});

					return (
						<Flex
							key={index}
							width="100%"
							border="1px solid"
							padding="0 0 40px"
							borderWidth="0 0 1px"
							borderColor="--border-primary"
						>
							<PromotionSuggestionCard
								{...suggestionData}
								posId={posId}
								loading={isQueued}
								itemState={stateHandler.get(suggestionData.simpleProductInfo.id)}
								onUndoTimeout={(simpleProductId) => stateHandler.update(simpleProductId, 'CHANGED')}
								updateSummary={summaryHandlers.update}
								showReplaceButton
								promotionHandlers={promotionHandlers}
								hasOnlyOneSuggestion={length === 1}
								handleChangeProductType={handleChangeSomeInto}
							/>
						</Flex>
					);
				})}
			</Flex>

			<Flex direction="column" padding={{ small: '16px', medium: '16px' }} boxShadow="2px 2px 8px #33333340">
				<SummaryAccordion summaryHandlers={summaryHandlers} />

				<Flex width="100%" justifyContent="flex-end">
					{stateHandler.hasSome('PARTIALLY_EVERY_CHANGED') ? (
						<UndoSuggestionCard
							onUndo={() => handleChangeEveryInto('SIMPLE')}
							loading={handleLoading().get(['EVERY'])}
							onTimeout={() => stateHandler.update('EVERY', 'PARTIALLY_EVERY_CHANGED', 'CHANGED')}
						/>
					) : (
						<Button
							onClick={() => handleChangeEveryInto('PROMOTION')}
							loading={hasSomeLoading}
							minWidth="175px"
							disabled={!stateHandler.hasSome('READY')}
						>
							<Flex
								as={Typography.Label.M}
								raw={{ cursor: 'inherit' }}
								lh100
								color={!stateHandler.hasSome('READY') ? '--text-disable' : '--text-invert'}
							>
								Substituir {replaceableQuantity > 1 ? 'todos' : 'por promoção'}
							</Flex>
						</Button>
					)}
				</Flex>
			</Flex>
		</Flex>
	);
}
